import { useRef } from "react";
import { Box } from "@mui/material";
import CustomDropzone, {
  DropzoneRejection,
} from "global/components/CustomDropzone/CustomDropzone";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import {
  COMPARE_EXTENSIONS,
  COMPARE_MAX_BYTES,
  fileProblem,
} from "../ContractCompareTypesAndValidation";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import { AlertCircleGlyph, UploadGlyph } from "./ContractCompareIcons";
import FileChip from "./FileChip";

interface UploadZoneProps {
  side: "old" | "new";
  file: File | null;
  /** Why the file was refused (the browser's check or the server's own words); the chip turns red. */
  problem: string | null;
  /** `file` is set even for a refused file, so its chip can say "not accepted" with the reason under it. */
  onFile: (file: File, problem: string | null) => void;
  onRemove: () => void;
  disabled?: boolean;
  /** Shown when a click lands on the disabled zone (D18): why the page is blocked. */
  disabledMessage?: string;
  /** A short drop area, for the old side (it sits under tabs and a chip). */
  compact?: boolean;
}

/**
 * One upload side. A shared `CustomDropzone` per side, each with its own `uniqueId` (D47: without one, both zones
 * share an input id and a click on the new-version zone would open the old one's picker -- the compare would run
 * backwards). The page does the 0-byte and single-file checks itself, and owns every error text through
 * `onReject` (D18/D50): the dropzone's own toast is not shown.
 */
const UploadZone = ({ side, file, problem, onFile, onRemove, disabled = false, disabledMessage, compact = false }: UploadZoneProps) => {
  const page = strings.contractCompare.page;
  const label = side === "old" ? page.dropOld : page.dropNew;
  const errorId = `compare-${side}-error`;
  const nameId = `compare-${side}-file-name`;
  // `onReject` runs before `onAdd` in the same drop: the rejection must survive the add that follows it.
  const rejectedThisDrop = useRef(false);

  const handleReject = (rejections: DropzoneRejection[]) => {
    const first = rejections[0];
    if (!first) return;
    rejectedThisDrop.current = true;
    void Promise.resolve().then(() => {
      rejectedThisDrop.current = false;
    });
    onFile(first.file, fileProblem(first.file) ?? strings.contractCompare.fileRefused);
  };

  const handleAdd = (files: File[]) => {
    if (rejectedThisDrop.current || !files.length) return;
    if (files.length > 1) {
      onFile(files[0], page.oneFileOnly);
      return;
    }
    onFile(files[0], fileProblem(files[0]));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      {file && (
        <FileChip file={file} nameId={nameId} invalid={!!problem} onRemove={onRemove} describedBy={problem ? errorId : undefined} />
      )}
      {problem && (
        <Box component="p" id={errorId} role="alert" sx={styles.fieldError} {...maskedContent}>
          <AlertCircleGlyph sx={{ width: 16, height: 16, mt: "1px", flexShrink: 0 }} />
          {problem}
        </Box>
      )}
      {(!file || problem) && (
        <CustomDropzone
          // Each side its own id: the hidden input is `drag&drop${uniqueId}` and the zone's label points at it.
          uniqueId={`compare-${side}`}
          // `dropzoneText` names the hidden file input only (this body replaces the zone's own text). No `ariaLabel`: it
          // would also name the drop surface, and a name that is only the title does not contain the visible
          // "or browse · PDF or Word, up to 10 MB" (WCAG 2.5.3). Without one the surface is named by that text.
          dropzoneText={label}
          sx={styles.dropBody(compact)}
          dragOverSx={styles.dropOver}
          onAdd={handleAdd}
          onReject={handleReject}
          maxFileSize={COMPARE_MAX_BYTES}
          acceptedFiles={[...COMPARE_EXTENSIONS]}
          showAlerts={false}
          multipleFileUpload={false}
          disabled={disabled}
          disabledMessage={disabledMessage}
          // Without this the dropzone prints "up to 9.9375 MB" from the byte limit.
          fileExtenstionText={page.dropLimits}
          innerBody={
            <>
              <Box sx={styles.dropIcon} aria-hidden="true">
                <UploadGlyph sx={{ width: t.dropzone.icon, height: t.dropzone.icon }} />
              </Box>
              <Box component="p" sx={styles.dropTitle}>
                {label}
              </Box>
              <Box component="p" sx={styles.dropMeta}>
                {page.dropOr} <Box component="span" sx={styles.browse}>{page.dropBrowse}</Box> · {page.dropLimits}
              </Box>
            </>
          }
        />
      )}
    </Box>
  );
};

export default UploadZone;
